import type { PlanSelection, TripCategory } from '../types/customer-portal';
import type { Destinations } from '../hooks/useDestinations';

/**
 * Determines the appropriate section based on destinations and trip category
 * @param destinations Array of destination objects with areaType
 * @param category Selected trip category
 * @returns Appropriate section for the given category and destinations
 */
export const detectSectionFromDestinations = (
  destinations: Destinations[],
  category: TripCategory
): PlanSelection['section'] => {
  if (category === 'International') {
    // Enhanced logic for International destinations
    const hasSchengen = destinations.some(dest => dest.areaType === 'schengen');
    const hasAsiaOceania = destinations.some(dest => dest.areaType === 'asiaOceania');
    
    // Priority logic: If any Schengen destination is selected, use WorldwideElite
    if (hasSchengen) {
      return 'WorldwideElite';
    }
    
    // If only Asia/Oceania destinations, use Asia and Oceania section
    if (hasAsiaOceania) {
      return 'Asia and Oceania';
    }
    
    // Default fallback for International (should rarely happen with proper destination data)
    return 'WorldwideElite';
  }
  
  if (category === 'Domestic') {
    // Default to Domestic section (ShortSecure logic can override based on transport mode)
    return 'Domestic';
  }
  
  // For Inbound, we use a default section (this will be handled by inbound-specific logic)
  return 'Domestic'; // Fallback
};

/**
 * Maps trip category to initial section for destination loading
 * @param category Selected trip category
 * @returns Initial section to use for loading destinations
 */
export const getInitialSectionForCategory = (category: TripCategory): PlanSelection['section'] => {
  switch (category) {
    case 'International':
      return 'WorldwideElite'; // Default to WorldwideElite, will auto-detect based on destinations
    case 'Domestic':
      return 'Domestic';
    case 'Inbound':
      return 'Domestic'; // Inbound uses special logic, not section-based
    default:
      return 'Domestic';
  }
};

/**
 * Determines if destinations should be loaded based on category and section
 * @param category Selected trip category
 * @param section Current section
 * @returns Whether to load destinations from the section-based hook
 */
export const shouldLoadDestinationsFromSection = (
  category: TripCategory,
  section?: PlanSelection['section']
): boolean => {
  // Inbound category uses getAllCountries hook instead of section-based destinations
  if (category === 'Inbound') {
    return false;
  }
  
  // International category without a pre-selected section should use combined destinations
  if (category === 'International' && !section) {
    return false;
  }
  
  // For other categories, load from section if available
  return !!section;
};

/**
 * Determines if the International destinations hook should be used
 * @param category Selected trip category
 * @param section Current section
 * @returns Whether to use the International destinations hook
 */
export const shouldUseInternationalDestinations = (
  category: TripCategory,
  section?: PlanSelection['section']
): boolean => {
  return category === 'International' && !section;
};

/**
 * Gets the scope parameter for API calls based on section
 * @param section Plan selection section
 * @returns API scope parameter
 */
export const getScopeFromSection = (section: PlanSelection['section']): string => {
  switch (section) {
    case 'Domestic':
      return 'local';
    case 'Asia and Oceania':
      return 'asiaOceania';
    case 'WorldwideElite':
      return 'schengen';
    case 'ShortSecure':
      return 'local';
    default:
      return 'local';
  }
};

/**
 * Determines the appropriate section based on transport mode for Domestic category
 * @param transportMode Selected transport mode
 * @returns Appropriate section for the given transport mode
 */
export const detectSectionFromTransportMode = (
  transportMode: 'land' | 'sea' | 'air'
): PlanSelection['section'] => {
  // Via Land and Via Sea map to ShortSecure section
  if (transportMode === 'land' || transportMode === 'sea') {
    return 'ShortSecure';
  }
  
  // Via Air maps to Domestic section
  if (transportMode === 'air') {
    return 'Domestic';
  }
  
  // Default fallback
  return 'Domestic';
};

/**
 * Determines if the current selection should use ShortSecure business logic
 * @param category Selected trip category
 * @param transportMode Selected transport mode (if applicable)
 * @returns Whether ShortSecure business logic should be applied
 */
export const shouldUseShortSecureLogic = (
  category: TripCategory,
  transportMode?: 'land' | 'sea' | 'air'
): boolean => {
  if (category === 'Domestic' && transportMode) {
    return transportMode === 'land' || transportMode === 'sea';
  }
  return false;
};

/**
 * Gets the effective section for a Domestic category selection
 * @param category Selected trip category
 * @param transportMode Selected transport mode (if applicable)
 * @returns The effective section to use for API calls and business logic
 */
export const getEffectiveSectionForDomestic = (
  category: TripCategory,
  transportMode?: 'land' | 'sea' | 'air'
): PlanSelection['section'] => {
  if (category === 'Domestic' && transportMode) {
    return detectSectionFromTransportMode(transportMode);
  }
  return 'Domestic';
};

/**
 * Determines if travel dates should be reset when transport mode changes
 * Only resets when switching between different plan sections (Domestic <-> ShortSecure)
 * @param previousTransportMode Previous transport mode
 * @param newTransportMode New transport mode
 * @returns Whether travel dates should be reset
 */
export const shouldResetTravelDatesOnTransportChange = (
  previousTransportMode: 'land' | 'sea' | 'air' | undefined,
  newTransportMode: 'land' | 'sea' | 'air'
): boolean => {
  if (!previousTransportMode) return false;
  
  const previousSection = detectSectionFromTransportMode(previousTransportMode);
  const newSection = detectSectionFromTransportMode(newTransportMode);
  
  // Reset dates only when switching between different sections
  // Domestic (air) <-> ShortSecure (land/sea)
  return previousSection !== newSection;
};

/**
 * Determines if travel dates should be reset when plan type changes within ShortSecure
 * ShortSecure Individual and Group plans have different date range restrictions:
 * - Individual: 15 days maximum (start date + 14 days)
 * - Group: 5 days maximum (start date + 4 days)
 * @param previousPlanType Previous plan type
 * @param newPlanType New plan type
 * @param isShortSecure Whether the current selection uses ShortSecure logic
 * @returns Whether travel dates should be reset
 */
export const shouldResetTravelDatesOnShortSecurePlanChange = (
  previousPlanType: 'Individual' | 'Group' | '' | undefined,
  newPlanType: 'Individual' | 'Group' | '',
  isShortSecure: boolean
): boolean => {
  if (!isShortSecure || !previousPlanType || !newPlanType || previousPlanType === newPlanType) {
    return false;
  }
  
  // Reset dates when switching between Individual and Group in ShortSecure
  // because they have different maximum date ranges
  return (previousPlanType === 'Individual' && newPlanType === 'Group') ||
         (previousPlanType === 'Group' && newPlanType === 'Individual');
};